import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import _get from "lodash.get";
import AddCircle from "@material-ui/icons/AddCircle";

import {
  closeModalDialog,
  openModalDialog,
  showSnackNotificationAction,
} from "../../../../actions/layoutActions";
import {
  performDeleteRequest,
  setReload,
} from "../../../../actions/dataComponentActions";
import propTypes from "../../../../constants/propTypes";
import {
  getDataComponent,
  getDataComponentFlattenedRequestState,
} from "../../../../reducers/dataComponentReducer";
import { getBluechipResourcesByType } from "../../../../utils/bluechipUtils";
import { Paper } from "../../../mui/core";
import { LoaderContext } from "../../../ui/Loader";
import { useDeleteProcessRequests } from "../../../hooks/useProcessRequest";
import { convertServerErrorToFormikErrors } from "../../../../utils/formValidationUtils";
import withRenderOnPermissionActive from "../../../hocs/withRenderOnPermissionActive";
import { BWGridAPI, GridHeader, GridTable } from "../../../ui/BWGrid";

export const columns = ["name"];

export const columnOptions = {
  name: { title: "Name", filter: true },
};

const rowMenu = ({ handleDelete, handleOpenEditModal }) => [
  {
    text: "Edit",
    onClick: handleOpenEditModal,
  },
  {
    text: "Delete",
    onClick: handleDelete,
  },
];

const actions = (label, onOpenCreateModal) => [
  {
    text: `Create ${label}`,
    icon: <AddCircle />,
    handler: onOpenCreateModal,
  },
];

// Delete errors come back keyed by the table name behind the route.
const getDeleteErrorSource = apiRoute => apiRoute.replace(/-/g, "_");

export const SettingsListSection = ({
  label,
  pluralLabel,
  model,
  apiRoute,
  dataComponentId,
  createModal,
  editModal,
  editModalEntityKey,
  dataComponent,
  flattenedDataComponent,
  loading,
  performDeleteRequest,
  setReload,
  showSnackNotificationAction,
  openModalDialog,
  closeModalDialog,
}) => {
  useDeleteProcessRequests(dataComponent, {
    onSuccess: () => {
      showSnackNotificationAction(`The ${label} has been deleted successfully`);
      setReload(dataComponentId, true);
    },
    onError: error => {
      const convertedError = convertServerErrorToFormikErrors(error);
      showSnackNotificationAction(
        convertedError[getDeleteErrorSource(apiRoute)]
      );
    },
  });

  const handleOpenCreateModal = useCallback(() => {
    openModalDialog(`Create ${label}`, createModal, {}, true, true);
  }, [createModal, label, openModalDialog]);

  const handleOpenEditModal = useCallback(
    entity => {
      openModalDialog(
        `Edit ${label}`,
        editModal,
        { [editModalEntityKey]: entity },
        true,
        true
      );
    },
    [editModal, editModalEntityKey, label, openModalDialog]
  );

  const handleDelete = useCallback(
    entity => {
      openModalDialog(
        `Delete ${label}`,
        "GeneralConfirmation",
        {
          title: `Deleting this ${label} will remove it from all linked projects as well. Are you sure?`,
          sendButtonText: "Confirm",
          onSubmit: () => {
            performDeleteRequest(dataComponentId, entity.id);
            closeModalDialog();
          },
        },
        true,
        true
      );
    },
    [
      closeModalDialog,
      dataComponentId,
      label,
      openModalDialog,
      performDeleteRequest,
    ]
  );

  return (
    <Paper>
      <LoaderContext.Provider value={loading}>
        <BWGridAPI
          dataComponent={flattenedDataComponent}
          model={model}
          defaultSorting={[
            {
              columnName: "name",
              direction: "asc",
            },
          ]}
          apiRoute={apiRoute}
          includes={[]}
        >
          <GridHeader
            headerText={`${flattenedDataComponent.totalRows} ${pluralLabel}`}
            actions={actions(label, handleOpenCreateModal)}
          />
          <GridTable
            columns={columns}
            columnOptions={columnOptions}
            rowMenu={rowMenu({
              handleDelete,
              handleOpenEditModal,
            })}
          />
        </BWGridAPI>
      </LoaderContext.Provider>
    </Paper>
  );
};

SettingsListSection.propTypes = {
  label: PropTypes.string.isRequired,
  pluralLabel: PropTypes.string.isRequired,
  model: PropTypes.func.isRequired,
  apiRoute: PropTypes.string.isRequired,
  dataComponentId: PropTypes.string.isRequired,
  createModal: PropTypes.string.isRequired,
  editModal: PropTypes.string.isRequired,
  editModalEntityKey: PropTypes.string.isRequired,
  dataComponent: propTypes.dataComponent,
  flattenedDataComponent: propTypes.dataComponent,
  loading: PropTypes.bool,
  openModalDialog: PropTypes.func.isRequired,
  performDeleteRequest: PropTypes.func.isRequired,
  setReload: PropTypes.func.isRequired,
  showSnackNotificationAction: PropTypes.func.isRequired,
  closeModalDialog: PropTypes.func.isRequired,
};

export const mapStateToProps = (state, { dataComponentId }) => {
  const flattenedDataComponent = getDataComponentFlattenedRequestState(
    dataComponentId,
    state
  );

  const dataComponent = getDataComponent(dataComponentId, state);

  const resources = getBluechipResourcesByType(dataComponentId, state) || [];

  return {
    dataComponent,
    flattenedDataComponent,
    loading: _get(flattenedDataComponent, "loading") || !resources,
  };
};

export const mapDispatchToProps = {
  performDeleteRequest,
  setReload,
  showSnackNotificationAction,
  openModalDialog,
  closeModalDialog,
};

const ConnectedSettingsListSection = connect(
  mapStateToProps,
  mapDispatchToProps
)(SettingsListSection);

/**
 * Builds a settings section: a grid with a single `name` column plus create,
 * edit and delete actions, rendered only for roles holding `permissions`.
 *
 * Usage:
 * export default createSettingsListSection({
 *   dataComponentId: scaleDataComponentId,
 *   model: Scale,
 *   apiRoute: "scales",
 *   label: "Scale",
 *   pluralLabel: "Scales",
 *   createModal: "CreateScale",
 *   editModal: "EditScale",
 *   editModalEntityKey: "scale",
 *   permissions: ["manage-scales"],
 * });
 */
export const createSettingsListSection = ({ permissions, ...config }) => {
  const Section = () => <ConnectedSettingsListSection {...config} />;
  Section.displayName = `${config.label.replace(/\s/g, "")}Section`;

  return withRenderOnPermissionActive(Section, permissions);
};
